import { getApiUrl } from '../config';
import { clearAuthData, getAuthToken, refreshToken } from '../authUtils';

/** API error with status code and response data */
export class ApiError extends Error {
  constructor(message, status, data = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/** 403 Forbidden - user authenticated but not authorized */
export class ForbiddenError extends ApiError {
  constructor(message, data = {}) {
    super(message, 403, data);
    this.name = 'ForbiddenError';
  }
}

/** 404 Not Found */
export class NotFoundError extends ApiError {
  constructor(message, data = {}) {
    super(message, 404, data);
    this.name = 'NotFoundError';
  }
}

/** 422 Unprocessable Entity — used for state-machine transition rejections.
 *  Body carries `code`, `current_step`, and `attempted_status` for inline UX.
 */
export class ValidationError extends ApiError {
  constructor(message, data = {}) {
    super(message, 422, data);
    this.name = 'ValidationError';
    this.code = data.code ?? null;
    this.currentStep = data.current_step ?? null;
    this.attemptedStatus = data.attempted_status ?? null;
  }
}

/**
 * Extract error message from various DRF response formats:
 * - {detail: "..."} - Standard DRF format
 * - {error: {message: "..."}} - Custom error format
 * - {non_field_errors: ["..."]} - Form validation errors
 * - {field_name: ["..."]} - Field-level validation errors
 */
function normalizeErrorMessage(data, fallback = 'An error occurred') {
  if (!data || typeof data !== 'object') {
    return fallback;
  }

  // Standard DRF detail format
  if (data.detail) {
    return data.detail;
  }

  // Custom error.message format
  if (data.error?.message) {
    return data.error.message;
  }

  // Non-field errors array
  if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
    return data.non_field_errors[0];
  }

  // Field-level errors - return first one found
  for (const key of Object.keys(data)) {
    if (Array.isArray(data[key]) && data[key].length > 0) {
      return `${key}: ${data[key][0]}`;
    }
  }

  return fallback;
}

// Prevents race condition when multiple API calls fail with 401 simultaneously
let isRedirecting = false;

/** Fetch wrapper with auth, silent 401 refresh-and-retry, and global redirect */
export async function apiFetch(endpoint, options = {}) {
  const token = getAuthToken();

  const response = await fetch(getApiUrl(endpoint), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  // On 401, attempt a silent token refresh and retry the request once
  if (response.status === 401 && !options._retried) {
    const newToken = await refreshToken();
    if (newToken) {
      return apiFetch(endpoint, { ...options, _retried: true });
    }

    // Refresh failed — redirect to login
    if (!isRedirecting) {
      isRedirecting = true;
      clearAuthData();
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?next=${next}`);
    }
    throw new ApiError('Authentication required', 401);
  }

  return response;
}

/** Fetch with JSON parsing and typed errors */
export async function apiFetchJson(endpoint, options = {}) {
  const response = await apiFetch(endpoint, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = normalizeErrorMessage(data, response.statusText);

    if (response.status === 403) throw new ForbiddenError(message, data);
    if (response.status === 404) throw new NotFoundError(message, data);
    if (response.status === 422) throw new ValidationError(message, data);
    throw new ApiError(message, response.status, data);
  }

  return data;
}

/** GET request */
export async function apiGet(endpoint) {
  return apiFetchJson(endpoint, { method: 'GET' });
}

/** POST request */
export async function apiPost(endpoint, data) {
  return apiFetchJson(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** PATCH request */
export async function apiPatch(endpoint, data) {
  return apiFetchJson(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** PUT request */
export async function apiPut(endpoint, data) {
  return apiFetchJson(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** DELETE request */
export async function apiDelete(endpoint) {
  return apiFetchJson(endpoint, { method: 'DELETE' });
}
